/**
 * tm/pages/MobileTaskSheet.tsx — 휴대폰 업체 화면(/m)의 진행 업무 한 줄을 누르면 아래에서 올라오는 판 (2026-10-28 지도사 「여기서 업무 종료 · 단계 이동 등도 가능하게 · 모바일에서도」)
 *  PC 대시보드 · 업체별 관리의 단계 버튼(FlowActions)과 같은 동작 · 같은 서버 규칙(services/stepFlow) · 되돌리기 10초도 같다
 *   ① 지금 단계: ✓ 완료(결과 단계면 ✓ 승인/선정 · 미승인/미선정) · 회신 받음 · +3일 · +7일 미루기(3번째부터 이유)
 *   ② 단계 이동: 단계 목록에서 하나 고르고 「옮기기」(두 번 눌러야 — 손가락 실수 막기) · 앞은 완료 · 뒤는 대기
 *      기한 바꾸기(2026-10-28 지도사 「모바일에서도 날짜 기한 설정 변경 가능해야」): 끝나지 않은 단계(지금 단계 포함)를 고르면 날짜 · 「이 단계만」 / 「뒤 단계도 같이」(서버 flow due)
 *   ③ 업무 끝내기: 업무 전체 완료 · 보류 · 중단(이유) · 지우기(한 번 더)
 *  확인 창 없음 — 위험한 것은 판 안에서 한 번 더 · 실행 뒤 판을 닫고 아래 「되돌리기」
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import type { FlowBody } from '../api';
import type { Company, Task, TaskStep } from '../types';
import { G, clamp } from '../portal-ui/tds';
import { CLOSE_REASONS, judgeOf, kstToday, md, sortedSteps, stepEx } from '../flow/flowLogic';
import { STOP_REASONS, notSentence, retryMonth } from '../flow/FlowActions';
import { Button, DatePick, Sheet, TOSS, input } from './mobileKit';

type Mode = 'main' | 'stop' | 'del' | 'no' | 'reason';
type Run = (task: Task, body: FlowBody, label?: string) => Promise<boolean>;

const secTitle = (t: string) => <div style={{ fontSize: 15, fontWeight: 700, color: TOSS.sub, margin: '20px 0 8px' }}>{t}</div>;
const grid2 = (children: ReactNode) => <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, marginTop: 8 }}>{children}</div>;
const red = (t: string) => <span style={{ color: TOSS.red }}>{t}</span>;

function stepNote(s: TaskStep, cur: boolean): { text: string; color: string } {
  if (cur) return { text: s.status === '회신대기' ? '지금 · 회신 대기' : '지금', color: TOSS.blue };
  if (s.status === '완료') { const d = stepEx(s).done_at; return { text: d ? `완료 ${md(d).replace(/\(.\)$/, '')}` : '완료', color: TOSS.faint }; }
  return { text: s.due ? `기한 ${md(s.due).replace(/\(.\)$/, '')}` : '대기', color: TOSS.sub };
}

export default function MobileTaskSheet({ task, comp, onClose, run }: { task: Task; comp: Company; onClose: () => void; run: Run }) {
  const [mode, setMode] = useState<Mode>('main');
  const [pick, setPick] = useState<string>('');
  const [due, setDue] = useState<string>('');   // 고른 단계의 새 기한
  const [other, setOther] = useState('');
  const [days, setDays] = useState(3);
  const [busy, setBusy] = useState(false);
  const ss = sortedSteps(task);
  const step = ss.find(s => s.status !== '완료') || null;
  const idx = step ? ss.indexOf(step) : -1;
  const next = idx >= 0 ? ss.slice(idx + 1).find(s => s.status !== '완료') : undefined;
  const judge = step ? judgeOf(step) : null;
  const postponed = Number(stepEx(step).postponed) || 0;
  const today = kstToday();

  /** 실행 → 판 닫기(되돌리기는 화면 아래) · 연타 막기 */
  const go = async (body: FlowBody) => {
    if (busy) return;
    setBusy(true);
    const okRun = await run(task, body, comp.name);
    setBusy(false);
    if (okRun) onClose();
  };
  const postpone = (n: number) => {
    if (!step) return;
    if (postponed >= 2) { setDays(n); setMode('reason'); return; }
    void go({ action: 'postpone', stepId: step.id, days: n });
  };
  const back = <button type="button" className="tds-btn" onClick={() => { setMode('main'); setOther(''); }} style={{ minHeight: 44, padding: 0, border: 'none', background: 'none', color: TOSS.blue, fontSize: 16, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>‹ 돌아가기</button>;
  const head = (
    <div style={{ fontSize: 15, color: TOSS.sub, lineHeight: 1.5, ...clamp(2) }}>
      {comp.name}{step ? ` · 지금 「${step.name}」` : ' · 단계를 모두 마쳤어요'}
    </div>
  );
  const chipBtn = (key: string, label: string, on: () => void) => (
    <button key={key} type="button" className="tds-btn" onClick={on}
      style={{ minHeight: 48, padding: '0 16px', borderRadius: 22, border: `1px solid ${G.g300}`, background: '#fff', color: TOSS.text, fontSize: 16, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', maxWidth: '100%', ...clamp(1) }}>{label}</button>
  );
  const otherBox = (verb: string, on: (r: string) => void) => (
    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
      <input value={other} onChange={e => setOther(e.target.value)} placeholder="기타 (직접 쓰기)" style={{ ...input, flex: 1 }} />
      {other.trim() && <span style={{ width: 88, flexShrink: 0 }}><Button kind="weak" h={52} onClick={() => on(other.trim())}>{verb}</Button></span>}
    </div>
  );

  let body: ReactNode;
  if (mode === 'stop') {
    body = (<>
      {back}
      <div style={{ fontSize: 17, fontWeight: 700, color: TOSS.text, marginTop: 4 }}>업체가 안 하기로 했나요?</div>
      <div style={{ fontSize: 15, color: TOSS.sub, marginTop: 4 }}>이유를 누르면 바로 중단돼요 · 기록은 남고 팀장님 화면엔 「중단」</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>{STOP_REASONS.map(r => chipBtn(r, r, () => void go({ action: 'stop', reason: r })))}</div>
      {otherBox('중단', r => void go({ action: 'stop', reason: r }))}
    </>);
  } else if (mode === 'del') {
    body = (<>
      {back}
      <div style={{ fontSize: 17, fontWeight: 700, color: TOSS.text, marginTop: 4 }}>「{task.type}」 업무를 지울까요?</div>
      <div style={{ fontSize: 15, color: TOSS.sub, marginTop: 4, lineHeight: 1.55 }}>단계 {ss.length}개도 함께 · 휴지통에 30일 보관해요. 끝난 업무라면 지우지 말고 「업무 전체 완료」가 기록이 남아요.</div>
      {grid2(<>
        <Button kind="gray" onClick={() => setMode('main')}>그대로 두기</Button>
        <button type="button" className="tds-btn" onClick={() => void go({ action: 'trash' })}
          style={{ height: 56, borderRadius: 16, border: 'none', background: TOSS.red, color: '#fff', fontSize: 17, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>지우기</button>
      </>)}
    </>);
  } else if (mode === 'no' && step && judge) {
    const months = [1, 2].map(k => retryMonth(today, k));
    const close = (reason: string) => void go({ action: 'close', stepId: step.id, result_word: judge.no, reason, result_at: today });
    body = (<>
      {back}
      <div style={{ fontSize: 17, fontWeight: 700, color: TOSS.text, marginTop: 4 }}>「{step.name}」 {judge.no} — 다음은 어떻게 하나요?</div>
      {secTitle('다시 신청')}
      {grid2(months.map(m => <Button key={m.date} kind="weak" onClick={() => void go({ action: 'retry', stepId: step.id, date: m.date, exact: false, result_word: judge.no, result_at: today })}>{m.label}</Button>))}
      <div style={{ fontSize: 15, color: TOSS.sub, marginTop: 8, lineHeight: 1.55 }}>대표님 · 팀장님 화면엔 「이번엔 {notSentence(judge.no)}. {months[0].m}월에 다시 신청해요」처럼 보여요.</div>
      {secTitle('이번엔 여기서 마감 · 이유')}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{CLOSE_REASONS.map(r => chipBtn(r, r, () => close(r)))}{chipBtn('none', '이유 없이 마감', () => close(''))}</div>
      {otherBox('마감', close)}
    </>);
  } else if (mode === 'reason' && step) {
    body = (<>
      {back}
      <div style={{ fontSize: 17, fontWeight: 700, color: TOSS.text, marginTop: 4 }}>{postponed + 1}번째 미루기(+{days}일) · 이유를 하나 고르세요</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
        {['내 일정이 밀림', '자료 준비 중'].map(r => chipBtn(r, r, () => void go({ action: 'postpone', stepId: step.id, days, reason: r })))}
        {chipBtn('wait', '업체 · 팀장님 답을 기다림 → 회신 대기', () => void go({ action: 'wait', stepId: step.id, wait_for: ['업체', 'FP'], question: '' }))}
      </div>
      <div style={{ marginTop: 12 }}>{chipBtn('stop', '업체가 안 하기로 함 → 중단', () => setMode('stop'))}</div>
    </>);
  } else {
    const target = ss.find(s => s.id === pick);
    body = (<>
      {head}
      {step && (<>
        {secTitle('지금 단계')}
        {step.status === '회신대기'
          ? <Button onClick={() => void go({ action: 'reply', stepId: step.id })}>회신 받음 — 다시 우리 차례</Button>
          : judge
            ? grid2(<>
                <Button onClick={() => void go({ action: 'done', stepId: step.id, result_word: judge.yes })}>✓ {judge.yes}</Button>
                <Button kind="gray" onClick={() => setMode('no')}>{judge.no}</Button>
              </>)
            : <Button onClick={() => void go({ action: 'done', stepId: step.id })}>{next ? `✓ 완료 · 다음 「${next.name}」` : '✓ 완료 · 마지막 단계'}</Button>}
        {step.status !== '회신대기' && grid2(<>
          <Button kind="weak" h={52} onClick={() => postpone(3)}>+3일 미루기</Button>
          <Button kind="weak" h={52} onClick={() => postpone(7)}>+7일 미루기</Button>
        </>)}
      </>)}

      {ss.length > 0 && (<>
        {secTitle(ss.length > 1 ? '단계 이동 · 기한' : '기한')}
        <div role="radiogroup" aria-label="옮길 단계" style={{ border: `1px solid ${TOSS.line}`, borderRadius: 16, overflow: 'hidden' }}>
          {ss.map((s, i) => {
            const cur = !!step && s.id === step.id;
            const on = s.id === pick;
            const n = stepNote(s, cur);
            return (
              <button key={s.id} type="button" role="radio" aria-checked={on} className="tds-row" onClick={() => { setPick(on ? '' : s.id); setDue(on ? '' : (s.due ? String(s.due).slice(0, 10) : '')); }}
                style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 56, padding: '8px 14px', border: 'none', borderTop: i ? `1px solid ${TOSS.line}` : 'none', background: on ? TOSS.blueLt : '#fff', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
                <span style={{ width: 26, height: 26, borderRadius: 13, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                  background: cur || on ? TOSS.blue : s.status === '완료' ? TOSS.bg : '#fff', color: cur || on ? '#fff' : TOSS.sub, border: cur || on || s.status === '완료' ? 'none' : `1px solid ${G.g300}` }}>{i + 1}</span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: cur || on ? 700 : 500, color: s.status === '완료' && !on ? TOSS.sub : TOSS.text, ...clamp(1) }}>{s.name}</span>
                <span style={{ flexShrink: 0, fontSize: 15, fontWeight: cur ? 700 : 500, color: n.color, fontVariantNumeric: 'tabular-nums' }}>{n.text}</span>
              </button>
            );
          })}
        </div>
        {target && target.status !== '완료' && (
          <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 16, background: TOSS.bg }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: TOSS.sub, marginBottom: 8 }}>「{target.name}」 기한{target.due ? ` · 지금 ${md(target.due).replace(/\(.\)$/, '')}` : ' · 아직 없음'}</div>
            <DatePick value={due} onChange={setDue} />
            {grid2(<>
              <Button kind="weak" h={52} muted={!due || due === String(target.due || '').slice(0, 10)} onClick={() => { if (due && due !== String(target.due || '').slice(0, 10)) void go({ action: 'due', stepId: target.id, date: due }); }}>이 단계만</Button>
              <Button kind="weak" h={52} muted={!due || due === String(target.due || '').slice(0, 10)} onClick={() => { if (due && due !== String(target.due || '').slice(0, 10)) void go({ action: 'due', stepId: target.id, date: due, shift: true }); }}>뒤 단계도 같이</Button>
            </>)}
          </div>
        )}
        {target && !(step && target.id === step.id)
          ? <div style={{ marginTop: 8 }}><Button onClick={() => void go({ action: 'goto', stepId: target.id })}>「{target.name}」 단계로 옮기기</Button></div>
          : !target && <div style={{ fontSize: 15, color: TOSS.sub, marginTop: 8 }}>단계를 누르면 「옮기기」 · 기한 바꾸기 · 옮기면 앞 단계는 완료, 뒤 단계는 대기로 바뀌어요</div>}
      </>)}

      {secTitle('업무 끝내기')}
      {grid2(<>
        <Button kind="gray" onClick={() => void go({ action: 'finish' })}>업무 전체 완료</Button>
        <Button kind="gray" onClick={() => void go({ action: 'hold' })}>보류 · 잠시 멈춤</Button>
        <Button kind="gray" onClick={() => setMode('stop')}>{red('중단')}</Button>
        <Button kind="gray" onClick={() => setMode('del')}>{red('지우기')}</Button>
      </>)}
    </>);
  }

  return (
    <Sheet open title={task.type} onClose={onClose}>
      <div aria-busy={busy} style={busy ? { opacity: 0.6, pointerEvents: 'none' } : undefined}>{body}</div>
    </Sheet>
  );
}
